// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { Alert, AlertTitle, Box } from '@mui/material'
import type React from 'react'
import { useTranslate } from 'react-admin'

/**
 * A list fetch that is MEANT to arrive whole, and the count the server said it
 * holds.
 *
 * The union is deliberately narrow. `metrics` is NOT a member and must not
 * become one: that fetch carries `filter: {}` against a multi-million-row table
 * with a 10k cap, so `loaded < total` is its permanent, correct state and a
 * notice about it would fire on every account forever — training vets to look
 * past the one banner that matters. Pets and devices are different: both are
 * meant to come back complete, so short delivery is a real anomaly. Encoding
 * that in the TYPE is cheaper than re-litigating it at each call site.
 */
export type TruncatableList = {
  resource: 'pets' | 'devices'
  loaded: number
  /** The server's own count. UNDEFINED means unknown — never "truncated". */
  total?: number
}

/**
 * Which of these lists came back short. Exported for tests and for callers that
 * need the answer without the UI.
 *
 * An absent `total` is UNKNOWN, not truncated. A banner that fires on an
 * ordinary response is a banner that gets ignored.
 */
export const truncatedLists = (lists: TruncatableList[]): TruncatableList[] =>
  lists.filter((l) => typeof l.total === 'number' && l.total > l.loaded)

/**
 * Non-blocking notice that a screen is rendering a SUBSET of the clinic.
 *
 * WHY THIS EXISTS: ada-core caps a devices read at DEVICES_MAX_LIMIT (1,000 for
 * a normal role, 25,000 for an extended-admin one) and logs "response is
 * TRUNCATED" — on the SERVER, while returning 200. Measured live 2026-08-19: a
 * fleet-wide account received 1,000 of 1,592 devices, and since those lists sort
 * by recency the OLDEST sensors are the ones dropped. A patient whose devices
 * all fall past the cap renders "No Device", indistinguishable from one that
 * never had a sensor.
 *
 * Shared by every screen that fetches a whole-clinic list, so the three of them
 * cannot drift into three different answers about the same truncation.
 */
export const DataTruncationNotice: React.FC<{ lists: TruncatableList[] }> = ({ lists }) => {
  const translate = useTranslate()
  const truncated = truncatedLists(lists)
  if (truncated.length === 0) return null

  return (
    <Alert severity="warning" data-testid="dashboard-truncation-notice" sx={{ mb: 2 }}>
      <AlertTitle>{translate('pos.dashboard.clinical.truncated.title')}</AlertTitle>
      {truncated.map((t) => (
        <Box key={t.resource}>
          {translate('pos.dashboard.clinical.truncated.detail', {
            loaded: t.loaded,
            total: t.total,
            resource: translate(`pos.dashboard.clinical.truncated.${t.resource}`),
          })}
        </Box>
      ))}
      <Box sx={{ mt: 0.5 }}>{translate('pos.dashboard.clinical.truncated.consequence')}</Box>
    </Alert>
  )
}

export default DataTruncationNotice
